import React, { useState } from 'react';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useCalendar } from '@/contexts/CalendarContext';
import { usePricing } from '@/hooks/use-pricing';
import { trackEvent } from '@/lib/analytics';
import { useIsMobile } from '@/hooks/use-mobile';
import ReservationDialog from './ReservationDialog';
import { DateRange } from "react-day-picker";

const StickyBookingBar = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPricingSection, setShowPricingSection] = useState(true);
  const isMobile = useIsMobile();
  
  const { isLoading, isDateDisabled } = useCalendar();
  const pricing = usePricing(dateRange?.from, dateRange?.to);

  // Calculer la prochaine date disponible
  const getNextAvailableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let checkDate = new Date(today);
    
    // Chercher jusqu'à 365 jours dans le futur
    for (let i = 0; i < 365; i++) {
      if (!isDateDisabled(checkDate)) {
        return checkDate;
      }
      checkDate = new Date(checkDate);
      checkDate.setDate(checkDate.getDate() + 1);
    }
    return today;
  };

  // Mois à afficher : dates sélectionnées ou prochaine disponible
  const calendarMonth = dateRange?.from || getNextAvailableDate();
  
  const handleSearchClick = () => {
    if (dateRange?.from && dateRange?.to) {
      trackEvent('dates_validated', {
        source: 'sticky_bar',
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString(),
        nights: pricing?.nights,
      });
      setReservationDialogOpen(true);
    } else {
      alert('Veuillez sélectionner des dates d\'arrivée et de départ');
    }
  };
  
  return <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 py-2 px-2">
        <div className="container-custom max-w-5xl mx-auto">
          <div className="flex flex-col gap-2">
            {/* Date selection and button on same row */}
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button id="date-range" variant="outline" className={cn("w-full justify-start text-left font-normal h-10", !dateRange?.from && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange?.from ? dateRange.to ? <>
                            {format(dateRange.from, "dd MMM", {
                        locale: fr
                      })} - {format(dateRange.to, "dd MMM yyyy", {
                        locale: fr
                      })}
                          </> : format(dateRange.from, "dd MMM yyyy", {
                      locale: fr
                    }) : isMobile ? "Sélectionner dates" : "Sélectionner les dates de séjour"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar 
                      mode="range" 
                      selected={dateRange} 
                      onSelect={range => {
                        setDateRange(range);
                        if (range?.from && range?.to) {
                          setCalendarOpen(false);
                          trackEvent('dates_selected', {
                            source: 'sticky_bar',
                            from: range.from.toISOString(),
                            to: range.to.toISOString(),
                          });
                        }
                      }}
                      disabled={date => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || isDateDisabled(date);
                      }} 
                      defaultMonth={calendarMonth}
                      fromMonth={new Date()} 
                      className="pointer-events-auto" 
                      initialFocus 
                      numberOfMonths={isMobile ? 1 : 2}
                      locale={fr}
                      weekStartsOn={1}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <Button onClick={handleSearchClick} disabled={isLoading || !dateRange?.from || !dateRange?.to} className="bg-[#86AA8E] hover:bg-opacity-90 h-10 whitespace-nowrap">
                {isLoading ? "Chargement..." : "Valider"}
              </Button>
            </div>
            
            {/* Période sélectionnée et prix */}
            {dateRange?.from && dateRange?.to && (
              <div className="space-y-2">
                {/* Toggle button */}
                <button
                  type="button"
                  onClick={() => setShowPricingSection(!showPricingSection)}
                  className="w-full flex items-center justify-between p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <span className="text-xs font-medium text-gray-700">
                    {pricing && !pricing.error && !pricing.requiresQuote 
                      ? `${pricing.nights} nuit(s) • Total: ${pricing.total}€`
                      : `${pricing?.nights || 0} nuit(s)`
                    }
                  </span>
                  {showPricingSection ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {showPricingSection && (
                  <>
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <p className="text-xs font-medium text-blue-900">
                        Période sélectionnée : du {dateRange.from.toLocaleDateString('fr-FR')} au {dateRange.to.toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-blue-700">
                        {pricing?.nights || 0} nuit(s)
                      </p>
                    </div>
                    
                    {pricing && (
                      <>
                        {pricing.error ? (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-900">{pricing.error}</p>
                          </div>
                        ) : pricing.requiresQuote ? (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-900">
                              Pour un séjour de plus de 7 nuits hors période estivale, nous vous contacterons avec un devis personnalisé.
                            </p>
                          </div>
                        ) : (
                          <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                            <div className="space-y-1 text-xs">
                              {showDetails && (
                                <>
                                  <div className="flex justify-between">
                                    <span className="text-gray-700">Hébergement ({pricing.nights} nuit{pricing.nights > 1 ? 's' : ''})</span>
                                    <span className="font-medium">{pricing.subtotal}€</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-700">Frais de ménage</span>
                                    <span className="font-medium">{pricing.cleaningFee}€</span>
                                  </div>
                                  <div className="pt-1 border-t border-green-300"></div>
                                </>
                              )}
                              <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-900">Total</span>
                                <span className="font-semibold text-green-700">{pricing.total}€</span>
                              </div>
                              <button 
                                type="button"
                                onClick={() => setShowDetails(!showDetails)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline mt-1"
                              >
                                {showDetails ? "Masquer les détails" : "Afficher les détails"}
                              </button>
                              {pricing.isSummer && showDetails && (
                                <p className="text-xs text-gray-600 pt-1">
                                  Période estivale - Réservation par semaine
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
                
                <p className="text-[10px] text-center text-gray-400 flex items-center justify-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  Aucun paiement en ligne — simple demande par email
                </p>
              </div>
            )}
            
          </div>
        </div>
      </div>

      {/* Dialogue de réservation */}
      <ReservationDialog open={reservationDialogOpen} onOpenChange={setReservationDialogOpen} arrivalDate={dateRange?.from} departureDate={dateRange?.to} pricing={pricing} />
    </>;
};
export default StickyBookingBar;
